import { ActionError, Button, titleCase } from '@westhetics/ui'
import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import { isApiError } from '@/lib/api'
import { errorMessage } from '@/lib/errors'
import { leaveTo } from '@/lib/external-redirect'
import { forgetReturnTo, rememberReturnTo } from '@/lib/return-to'
import { roleLabel, useSession } from '@/lib/session'
import { routes } from '@/routes'
import { useAuthorization, useDecideAuthorization, type OAuthAuthorization } from './api'
import { AuthLayout, AuthSkeleton, authLink, authNote } from './auth-layout'

/**
 * Consentimento OAuth de um cliente MCP (docs/api/mcp.md, "Consentimento e conexões"; RN-AUT-24).
 * O Supabase Auth manda o navegador para cá com `?authorization_id=…`: a pessoa vê o nome do
 * aplicativo, o endereço de volta e a clínica em que a conexão vai operar, e responde. Só vale com a
 * sessão do app; quem chega sem ela entra primeiro e volta ao pedido (`lib/return-to.ts`). Segue o
 * leiaute do login (P-AUT-12).
 */

const MESSAGES = {
  noRequest:
    'Este endereço só funciona com o pedido de conexão que o seu aplicativo cria. Volte a ele e conecte de novo.',
  // 410 e 422 dizem a mesma coisa para quem está na tela: o pedido não vale mais.
  expired:
    'Este pedido de conexão venceu ou já foi respondido. Volte ao seu cliente MCP e conecte de novo.',
} as const

const TITLE = 'Conectar à sua conta'

export function OAuthConsentPage() {
  const session = useSession()
  const location = useLocation()
  const id = new URLSearchParams(location.search).get('authorization_id')
  const here = `${location.pathname}${location.search}`
  const isSignedIn = session.status === 'signed_in'
  const request = useAuthorization(id, isSignedIn)
  const decide = useDecideAuthorization(id ?? '')
  // Depois da resposta o navegador sai do app: a tela fica no rótulo de espera até lá.
  const [leaving, setLeaving] = useState<'approve' | 'deny' | null>(null)

  // A pessoa chegou ao pedido: o caminho guardado para depois do login já cumpriu o papel.
  useEffect(() => {
    if (isSignedIn) forgetReturnTo()
  }, [isSignedIn])

  // O mesmo aplicativo já foi autorizado antes: volta a ele sem perguntar de novo.
  const alreadyAuthorizedUrl =
    request.data?.status === 'already_authorized' ? request.data.redirectUrl : null
  useEffect(() => {
    if (alreadyAuthorizedUrl) leaveTo(alreadyAuthorizedUrl)
  }, [alreadyAuthorizedUrl])

  if (!id) return <Unavailable message={MESSAGES.noRequest} />
  if (session.status === 'loading') return <Waiting />
  // Sem sessão, ou com a senha temporária por trocar: resolve isso primeiro e volta ao pedido.
  if (session.status === 'signed_out') return <ComeBackAfter to={routes.login} returnTo={here} />
  if (session.status === 'password_required') {
    return <ComeBackAfter to={routes.newPassword} returnTo={here} />
  }

  if (request.isError || decide.isError) {
    const error = request.error ?? decide.error
    const isGone = isApiError(error) && (error.status === 410 || error.status === 422)
    if (isGone) {
      return (
        <Unavailable
          message={isApiError(error) && error.status === 410 ? error.message : MESSAGES.expired}
        />
      )
    }
  }
  if (request.isError) {
    return (
      <AuthLayout title={TITLE}>
        <ActionError
          className="mt-6"
          title="Não conseguimos carregar o pedido de conexão"
          description={errorMessage(request.error)}
          actions={
            <Button variant="outline" size="sm" onClick={() => void request.refetch()}>
              Tentar de novo
            </Button>
          }
        />
      </AuthLayout>
    )
  }
  if (!request.data) return <Waiting />
  if (request.data.status === 'already_authorized') {
    return (
      <AuthLayout title={TITLE}>
        <p className="font-body text-secondary mt-3 text-[13px] leading-[1.6] font-light">
          Este aplicativo já estava conectado à sua conta. Voltando a ele…
        </p>
      </AuthLayout>
    )
  }

  const authorization = request.data.authorization
  const client = authorization.oauthClient.name
  const busy = decide.isPending || leaving !== null

  function answer(decision: 'approve' | 'deny') {
    if (busy) return
    setLeaving(decision)
    decide.mutate(decision, {
      onSuccess: (result) => leaveTo(result.redirectUrl),
      onError: () => setLeaving(null),
    })
  }

  return (
    <AuthLayout
      title={`Conectar o ${client} à sua conta?`}
      onSubmit={(event) => {
        event.preventDefault()
        answer('approve')
      }}
    >
      <p className="font-body text-secondary mt-3 text-[13px] leading-[1.6] font-light">
        {reach(authorization)} Tudo o que você não pode fazer no sistema, ele também não pode.
      </p>

      <div className="eyebrow mt-8">Endereço de volta</div>
      <p className="font-body text-ink border-hairline-strong mt-2 border-b pb-3 text-[13px] leading-[1.5] font-light break-all">
        {authorization.redirectUri}
      </p>
      <p className={`${authNote} mt-2`}>
        Depois da resposta, o navegador volta para este endereço. Se você não reconhece o aplicativo
        ou o endereço, não conecte.
      </p>

      <p className={`${authNote} mt-6`}>
        Você está como{' '}
        <span className="text-ink">
          {titleCase(authorization.user.name)} · {authorization.user.email}
        </span>
        .{' '}
        <button
          type="button"
          disabled={busy}
          onClick={() => void session.signOut()}
          className="text-ink border-hairline-heavy hover:border-ink cursor-pointer border-b pb-px disabled:cursor-default"
        >
          Não é você? Sair
        </button>
      </p>

      {decide.isError ? (
        <ActionError
          className="mt-6"
          title="Não foi possível responder ao pedido"
          description={errorMessage(decide.error)}
        />
      ) : null}

      <Button
        type="submit"
        size="lg"
        className="mt-7"
        disabled={leaving === 'deny'}
        loading={leaving === 'approve'}
        loadingLabel="Conectando…"
      >
        {leaving === 'deny' ? 'Voltando ao aplicativo sem conectar' : 'Conectar'}
      </Button>
      <button
        type="button"
        disabled={busy}
        onClick={() => answer('deny')}
        className={`${authLink} mt-5 disabled:cursor-default`}
      >
        Agora não
      </button>
      <p className={`${authNote} mt-6`}>
        A conexão vale por 12 horas. Depois disso, o aplicativo pede para conectar de novo.
      </p>
    </AuthLayout>
  )
}

/** O que a conexão alcança: a clínica e o perfil da pessoa, ou a visão global do root (P-AUT-13). */
function reach(authorization: OAuthAuthorization) {
  const client = authorization.oauthClient.name
  if (authorization.isRoot) {
    return `O ${client} vai poder ver o que você vê em visão global, como root.`
  }
  if (!authorization.clinic) return `O ${client} vai poder ver o que você vê, com o seu perfil.`
  const { name, role } = authorization.clinic
  return `O ${client} vai poder ver o que você vê na ${name}, com o seu perfil de ${roleLabel(role).toLowerCase()}.`
}

function Waiting() {
  return (
    <AuthLayout title={TITLE}>
      <AuthSkeleton />
    </AuthLayout>
  )
}

/** O pedido não vale mais (ou nunca existiu): nome, razão e saída. */
function Unavailable({ message }: { message: string }) {
  return (
    <AuthLayout title="Pedido de conexão indisponível">
      <p className="font-body text-secondary mt-3 text-[13px] leading-[1.6] font-light">
        {message}
      </p>
      <Link to="/" className={`${authLink} mt-6`}>
        Ir para o sistema
      </Link>
    </AuthLayout>
  )
}

/** Guarda o caminho do pedido e manda a pessoa resolver o que falta (entrar, trocar a senha). */
function ComeBackAfter({ to, returnTo }: { to: string; returnTo: string }) {
  const navigate = useNavigate()
  useEffect(() => {
    rememberReturnTo(returnTo)
    navigate(to, { replace: true })
  }, [navigate, to, returnTo])
  return <Waiting />
}
